// Shared bits for the item page pieces (organisations, people, dates).
export type Org = { id: string; name: string; short_code: string | null; aliases: string[] | null; kind: string | null };
export type Named = { id: string; name: string; short_code: string | null; aliases: string[] | null };
export type Person = { id: string; full_name: string | null; email: string | null };

/** Short label for chips and lists: the name when short ("Lake|Flato"), else its short code ("PBS", "SWI"). */
export const orgShort = (o: Org | undefined | null) => (!o ? '—' : o.name.length <= 12 || !o.short_code ? o.name : o.short_code);
export const personName = (p: Person | undefined | null) => (p ? p.full_name || p.email || 'Someone' : '—');

export const today = () => new Date().toISOString().slice(0, 10);

/** Whole days between a date/time and now (never negative). */
export function daysSince(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = new Date(v.length <= 10 ? v + 'T00:00:00' : v).getTime();
  if (Number.isNaN(t)) return null;
  return Math.max(0, Math.floor((Date.now() - t) / 86_400_000));
}

/** Days until a date (negative = overdue). */
export function daysUntil(v: string | null | undefined): number | null {
  if (!v) return null;
  const t = new Date(v + 'T00:00:00').getTime();
  const now = new Date(today() + 'T00:00:00').getTime();
  return Math.round((t - now) / 86_400_000);
}

export const plural = (n: number, one: string, many = one + 's') => `${n} ${n === 1 ? one : many}`;

/** "16 days left" / "due today" / "3 days overdue" */
export function dueText(v: string | null | undefined): { text: string; tone: 'late' | 'soon' | 'ok' } | null {
  const d = daysUntil(v);
  if (d === null) return null;
  if (d < 0) return { text: `${plural(-d, 'day')} overdue`, tone: 'late' };
  if (d === 0) return { text: 'due today', tone: 'soon' };
  return { text: `${plural(d, 'day')} left`, tone: d <= 3 ? 'soon' : 'ok' };
}

/** Light name similarity for "did you mean…" suggestions on unmatched imported names. */
export function suggest<T extends Named>(raw: string, list: T[]): T | undefined {
  const n = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');
  const r = n(raw);
  if (!r) return undefined;
  let best: { t: T; s: number } | undefined;
  for (const t of list) {
    for (const k of [t.name, t.short_code ?? '', ...(t.aliases ?? [])].map(n).filter(Boolean)) {
      let s = 0;
      if (k === r) s = 100;
      else if (k.startsWith(r) || r.startsWith(k)) s = 60 + Math.min(k.length, r.length);
      else if (k.includes(r) || r.includes(k)) s = 40 + Math.min(k.length, r.length);
      else {
        // shared words
        const a = new Set(raw.toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2));
        const b = [t.name, ...(t.aliases ?? [])].join(' ').toLowerCase().split(/[^a-z0-9]+/).filter((w) => w.length > 2);
        s = b.filter((w) => a.has(w)).length * 20;
      }
      if (s > (best?.s ?? 19)) best = { t, s };
    }
  }
  return best?.t;
}
